import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import classNames from "classnames";
import HDSwitch from "@/common/components/HDSwitch";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { useImpersonationAwareUser } from "@/common/hooks/useImpersonationAwareUser";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { fetchAllDocuments } from "@/state/redux/documentSlice";
import type { DocumentType } from "@common/utils/types";
import { avimarkExportEditorUrl } from "@common/utils/url-registry";
import { useAvimarkDocuments } from "../hooks/useAvimarkDocuments";
import { hubDocumentRow, showNewClinicLine, type HubDocumentStatus } from "./documentStatus";

const CHIP: Record<HubDocumentStatus, [string, string]> = {
    ready: ["Ready", "bg-max-50 text-max-950 ring-max-200"],
    "needs-setup": ["Needs setup", "bg-tropicana-50 text-tropicana-900 ring-tropicana-300"],
    "export-off": ["Export off", "bg-gray-100 text-gray-600 ring-gray-200"],
    "built-in": ["Built in", "bg-gray-100 text-gray-600 ring-gray-200"],
};

/** EN-1249 F10: "Documents sent to AVImark". The same list Settings › Documents shows. */
export default function DocumentsBlock() {
    const dispatch = useAppDispatch();
    const { accountData } = useAccountContext();
    const { userId } = useImpersonationAwareUser();
    const { documentTypes, hiddenDocuments, status: listStatus } = useAppSelector((s) => s.documents);
    const listFailed = listStatus === "failed" && documentTypes.length === 0;

    const [listAttempt, setListAttempt] = useState(0);
    // Mirrors DocumentTypes.tsx: the list may not be loaded when Settings opens on Integrations.
    useEffect(() => {
        if (!userId || documentTypes.length > 0) return undefined;
        const controller = new AbortController();
        dispatch(fetchAllDocuments({ accountID: userId, signal: controller.signal }));
        return () => controller.abort();
    }, [userId, documentTypes.length, listAttempt]);

    const docs = useMemo(
        () =>
            (documentTypes as DocumentType[]).filter((d) =>
                // Same rule as Settings › Documents: shared documents are on unless hidden, own ones follow is_active.
                d.account_id !== accountData?.id ? !hiddenDocuments.includes(d.id) : d.is_active,
            ),
        [documentTypes, hiddenDocuments, accountData?.id],
    );
    const ids = useMemo(() => docs.map((d) => d.id), [docs]);
    const { byId, isLoading, isError, refetch, setActive, switchError } = useAvimarkDocuments(ids, true);
    const rows = docs.map((d) => ({ doc: d, documentTypeId: d.id, row: hubDocumentRow(d.id, byId.get(d.id)) }));

    return (
        <section className="flex flex-col gap-3" aria-labelledby="avimark-docs">
            <div>
                <h3 id="avimark-docs" className="text-base font-semibold text-gray-900">
                    Documents sent to AVImark
                </h3>
                <p className="text-sm text-gray-600">
                    Choose which documents export, and where each part of the note goes in AVImark.
                </p>
            </div>
            {/* No rows means the list hasn't loaded, not a new clinic. */}
            {rows.length > 0 && !isLoading && !isError && showNewClinicLine(rows) && (
                <p className="text-sm text-gray-700">
                    Set up each document you want sent to AVImark. HappyDoc won't export a document until it's set up.
                </p>
            )}
            {listFailed && (
                <p className="text-sm text-gray-700">
                    Couldn't load your documents.{" "}
                    <button type="button" className="underline" onClick={() => setListAttempt((n) => n + 1)}>
                        Try again
                    </button>
                </p>
            )}
            {isError && (
                <p className="text-sm text-gray-700">
                    Couldn't load your documents.{" "}
                    <button type="button" className="underline" onClick={refetch}>
                        Try again
                    </button>
                </p>
            )}
            {switchError && (
                <p role="alert" className="text-sm text-red-700">
                    {switchError}
                </p>
            )}
            <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-max-800">
                        <tr>
                            <th className="w-16 px-3 py-3 lg:px-4">Export</th>
                            <th className="px-3 py-3 lg:px-4">Document</th>
                            <th className="hidden px-4 py-3 lg:table-cell">Status</th>
                            {/* Phones can't use the editor (the export gate says to set up on a computer). */}
                            <th className="hidden px-3 py-3 sm:table-cell lg:px-4">
                                <span className="sr-only">Action</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ doc, row }) => {
                            const summary = byId.get(doc.id);
                            const [chip, chipClass] = CHIP[row.status];
                            const statusChip = isError ? (
                                <span className="text-gray-500" aria-label="Status unavailable">
                                    -
                                </span>
                            ) : (
                                <span
                                    className={classNames(
                                        "inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                                        chipClass,
                                    )}
                                >
                                    {chip}
                                </span>
                            );
                            return (
                                <tr key={doc.id} className="border-t border-gray-200">
                                    <td className="px-3 py-3 lg:px-4">
                                        {row.status === "built-in" ? null : (
                                            <HDSwitch
                                                checked={summary?.isActive ?? false}
                                                disabled={isLoading || isError || !summary}
                                                onChange={(next: boolean) => setActive(doc.id, next)}
                                                srLabel={`Export ${doc.document_type_name} to AVImark`}
                                            />
                                        )}
                                    </td>
                                    <td className="px-3 py-3 lg:px-4">
                                        <div className="font-medium text-gray-900">{doc.document_type_name}</div>
                                        <div className="text-xs text-gray-500">
                                            {isLoading ? "Loading..." : isError ? "" : row.detail}
                                        </div>
                                        {/* Below lg the Status column is hidden (display none, so read once) and the chip shows here instead. */}
                                        <div className="mt-1 lg:hidden">{statusChip}</div>
                                    </td>
                                    <td className="hidden px-4 py-3 lg:table-cell">{statusChip}</td>
                                    <td className="hidden px-3 py-3 text-right sm:table-cell lg:px-4">
                                        {!isError && row.actionLabel && (
                                            <Link
                                                className={classNames(
                                                    "whitespace-nowrap",
                                                    row.status === "needs-setup"
                                                        ? "primary-button"
                                                        : "secondary-button",
                                                )}
                                                to={avimarkExportEditorUrl(doc.id, "hub")}
                                                aria-label={`${row.actionLabel} ${doc.document_type_name}`}
                                            >
                                                {row.actionLabel}
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
